/*
 * Author: Dw
 * File Created: Wednesday, 25-05-28 02:07 pm
 * Last Modified: Wednesday, 25-06-11 06:08 pm, by Dw
 */

// metrics/cpuUsage.ts

// eslint-disable-next-line unicorn/prefer-node-protocol -- ignore ESM import
import os from 'os';

import type { ObservableResult } from '@opentelemetry/api';

// ==================== 模块级状态 ====================
let previousTimestamp: bigint | null = null;
let previousUsage: null | NodeJS.CpuUsage = null; // { user: μs, system: μs }
let lastValue = 0;

if (typeof process.cpuUsage === 'function') {
  previousUsage = process.cpuUsage();
}

// 采样最小间隔：3 秒
const MIN_INTERVAL_NS = BigInt(3 * 1e9);

/**
 * 返回一组固定的 labels，用于所有 observe 调用
 */
function getMeta(): Record<string, string> {
  return {
    pid: process.pid.toString(),
    host: os.hostname(),
    cpu_cores: os.cpus().length.toString(),
    platform: process.platform,
  };
}

/**
 * ObservableGauge 的回调：差分计算出最近一次采样周期的 CPU 利用率
 */
export function cpuObservableGauge(observableResult: ObservableResult) {
  const nowTs = process.hrtime.bigint(); // 当前时间（纳秒）
  const nowUsage = process.cpuUsage(); // 当前 CPU 累积时间（μs）

  // 第一次采样：初始化基准并上报 0
  if (previousTimestamp === null) {
    previousTimestamp = nowTs;
    previousUsage = nowUsage;
    lastValue = 0;
    observableResult.observe(0, getMeta());

    return;
  }

  const deltaNs = nowTs - previousTimestamp;

  // 如果距离上次采样还不到 MIN_INTERVAL_NS，就重复上次值
  if (deltaNs < MIN_INTERVAL_NS) {
    observableResult.observe(lastValue, getMeta());

    return;
  }

  // 增量墙钟时间（秒）
  const deltaSec = Number(deltaNs) / 1e9;
  // 增量 CPU 时间（秒）
  const deltaCpuMicros =
    nowUsage.user -
    (previousUsage?.user || 0) +
    (nowUsage.system - (previousUsage?.system || 0));

  const deltaCpuSec = deltaCpuMicros / 1e6;

  // 计算百分比：增量 CPU 秒 / (增量墙钟秒 × 核数) × 100
  const cores = os.cpus().length;
  const rawPercent = (deltaCpuSec / (deltaSec * cores)) * 100;

  // 指数平滑
  lastValue = 0.2 * rawPercent + 0.8 * lastValue;

  // 上报
  observableResult.observe(lastValue, getMeta());

  // 更新基准
  previousTimestamp = nowTs;
  previousUsage = nowUsage;
}
